/**
 * Theme preference.
 *
 * `index.html` applies the stored theme before first paint using the same key
 * and the same rule (`system` follows the OS media query). This module keeps
 * that decision alive for the rest of the session: it owns the media-query
 * listener, writes the preference back to localStorage, and lets components
 * subscribe without prop drilling.
 */

import { useCallback, useSyncExternalStore } from 'react'

import type { Theme } from '@/api/types'

const STORAGE_KEY = 'tunes.theme'

const THEMES: readonly Theme[] = ['system', 'light', 'dark']

function isTheme(value: unknown): value is Theme {
  return typeof value === 'string' && (THEMES as readonly string[]).includes(value)
}

function readStored(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return isTheme(stored) ? stored : 'system'
  } catch {
    // Safari in private mode throws on localStorage access.
    return 'system'
  }
}

const prefersDark =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null

function systemIsDark(): boolean {
  return prefersDark?.matches ?? false
}

export type ResolvedTheme = 'light' | 'dark'

export function resolveTheme(theme: Theme): ResolvedTheme {
  if (theme === 'system') return systemIsDark() ? 'dark' : 'light'
  return theme
}

/** Toggle the `.dark` class on <html>. Every colour token flips from there. */
export function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return
  document.documentElement.classList.toggle('dark', resolveTheme(theme) === 'dark')
}

// --- a tiny external store so every consumer stays in step ------------------

let currentTheme: Theme = readStored()
let snapshot: { theme: Theme; resolved: ResolvedTheme } = {
  theme: currentTheme,
  resolved: resolveTheme(currentTheme),
}

const listeners = new Set<() => void>()

function refresh(): void {
  const resolved = resolveTheme(currentTheme)
  if (snapshot.theme === currentTheme && snapshot.resolved === resolved) return
  snapshot = { theme: currentTheme, resolved }
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot(): { theme: Theme; resolved: ResolvedTheme } {
  return snapshot
}

export function getTheme(): Theme {
  return currentTheme
}

export function setTheme(theme: Theme): void {
  currentTheme = theme
  try {
    localStorage.setItem(STORAGE_KEY, theme)
  } catch {
    // A missing preference just means "system" on the next load.
  }
  applyTheme(theme)
  refresh()
}

// The OS can flip while the app is open; only `system` should follow it.
prefersDark?.addEventListener('change', () => {
  if (currentTheme === 'system') {
    applyTheme('system')
    refresh()
  }
})

// Another tab changing the preference should not leave this one out of sync.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return
    currentTheme = isTheme(event.newValue) ? event.newValue : 'system'
    applyTheme(currentTheme)
    refresh()
  })
}

export interface UseThemeResult {
  /** The stored preference: what the settings UI should show as selected. */
  theme: Theme
  /** What is actually on screen right now. */
  resolved: ResolvedTheme
  setTheme: (theme: Theme) => void
  /** Convenience for a light/dark switch: skips `system`. */
  toggle: () => void
}

export function useTheme(): UseThemeResult {
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  const toggle = useCallback(() => {
    setTheme(resolveTheme(getTheme()) === 'dark' ? 'light' : 'dark')
  }, [])

  return { theme: state.theme, resolved: state.resolved, setTheme, toggle }
}
